import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';

export interface SecuritySettings {
  user_switching: 'never' | 'barcode_exempt' | 'require_password_higher_privilege' | 'always';
  default_auth_method: 'password' | 'mfa';
  inactivity_timeout_minutes: number | null;
  require_reauth_on_inactivity: boolean;
  password_min_length: number;
  password_require_uppercase: boolean;
  password_require_number: boolean;
  password_require_special: boolean;
  session_timeout_hours: number;
}

export interface RoleAuthSetting {
  id: string;
  organization_id: string;
  role: string;
  login_access: 'full' | 'switch_only';
  auth_method: 'default' | 'password' | 'mfa';
  allow_quick_login: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserSwitchLog {
  id: string;
  organization_id: string;
  location_id: string | null;
  register_id: string | null;
  from_user_id: string | null;
  from_user_role: string | null;
  to_user_id: string;
  to_user_role: string | null;
  switch_method: 'password' | 'pin' | 'barcode' | 'none';
  auth_required: boolean;
  auth_provided: boolean;
  created_at: string;
  from_user?: { first_name: string; last_name: string } | null;
  to_user?: { first_name: string; last_name: string } | null;
}

const DEFAULT_SECURITY_SETTINGS: SecuritySettings = {
  user_switching: 'require_password_higher_privilege',
  default_auth_method: 'password',
  inactivity_timeout_minutes: null,
  require_reauth_on_inactivity: false,
  password_min_length: 8,
  password_require_uppercase: false,
  password_require_number: false,
  password_require_special: false,
  session_timeout_hours: 24,
};

const STANDARD_ROLES = ['superadmin', 'owner', 'admin', 'manager', 'cashier', 'employee', 'inventory', 'inventory_manager'];

export function useSecuritySettings() {
  const { organization } = useAuth();
  const queryClient = useQueryClient();
  const organizationId = organization?.id;

  // Fetch security settings
  const {
    data: securitySettings,
    isLoading: settingsLoading,
    error: settingsError,
  } = useQuery({
    queryKey: ['security-settings', organizationId],
    queryFn: async () => {
      if (!organizationId) return DEFAULT_SECURITY_SETTINGS;

      const { data, error } = await supabase
        .from('organizations')
        .select('security_settings')
        .eq('id', organizationId)
        .single();

      if (error) throw error;
      return { ...DEFAULT_SECURITY_SETTINGS, ...(data?.security_settings || {}) } as SecuritySettings;
    },
    enabled: !!organizationId,
  });

  // Fetch role auth settings
  const {
    data: roleAuthSettings,
    isLoading: roleSettingsLoading,
    refetch: refetchRoleSettings,
  } = useQuery({
    queryKey: ['role-auth-settings', organizationId],
    queryFn: async () => {
      if (!organizationId) return [];

      const { data, error } = await supabase
        .from('role_auth_settings')
        .select('*')
        .eq('organization_id', organizationId)
        .order('role');

      if (error) throw error;
      return data as RoleAuthSetting[];
    },
    enabled: !!organizationId,
  });

  // Fetch user switch logs
  const {
    data: switchLogs,
    isLoading: logsLoading,
  } = useQuery({
    queryKey: ['user-switch-logs', organizationId],
    queryFn: async () => {
      if (!organizationId) return [];

      const { data, error } = await supabase
        .from('user_switch_logs')
        .select(`
          *,
          from_user:profiles!user_switch_logs_from_user_id_fkey(first_name, last_name),
          to_user:profiles!user_switch_logs_to_user_id_fkey(first_name, last_name)
        `)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      return data as UserSwitchLog[];
    },
    enabled: !!organizationId,
  });

  // Update security settings
  const updateSecuritySettings = useMutation({
    mutationFn: async (updates: Partial<SecuritySettings>) => {
      if (!organizationId) throw new Error('No organization');

      const { error } = await supabase
        .from('organizations')
        .update({
          security_settings: {
            ...securitySettings,
            ...updates,
          },
        })
        .eq('id', organizationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['security-settings', organizationId] });
    },
  });

  // Update role auth setting
  const updateRoleAuthSetting = useMutation({
    mutationFn: async ({
      role,
      updates,
    }: {
      role: string;
      updates: Partial<Pick<RoleAuthSetting, 'login_access' | 'auth_method' | 'allow_quick_login'>>;
    }) => {
      if (!organizationId) throw new Error('No organization');

      const { error } = await supabase
        .from('role_auth_settings')
        .upsert({
          organization_id: organizationId,
          role,
          ...updates,
        }, {
          onConflict: 'organization_id,role',
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role-auth-settings', organizationId] });
    },
  });

  // Get settings for a specific role
  const getRoleSettings = (role: string): RoleAuthSetting | undefined => {
    return roleAuthSettings?.find(r => r.role === role);
  };

  // Get effective auth method for a role
  const getEffectiveAuthMethod = (role: string): 'password' | 'mfa' => {
    const roleSetting = getRoleSettings(role);
    if (roleSetting?.auth_method && roleSetting.auth_method !== 'default') {
      return roleSetting.auth_method;
    }
    return securitySettings?.default_auth_method || 'password';
  };

  return {
    // Data
    securitySettings: securitySettings || DEFAULT_SECURITY_SETTINGS,
    roleAuthSettings: roleAuthSettings || [],
    switchLogs: switchLogs || [],
    standardRoles: STANDARD_ROLES,

    // Loading states
    isLoading: settingsLoading || roleSettingsLoading,
    logsLoading,
    settingsError,

    // Mutations
    updateSecuritySettings,
    updateRoleAuthSetting,
    refetchRoleSettings,

    // Helpers
    getRoleSettings,
    getEffectiveAuthMethod,
  };
}

















